/**
 * Filter card for the Product Catalogue Review tabs that need rich filtering.
 *
 * Mirrors the visual structure of `DataViewFilterPanel` (the reference
 * page for the layout) but takes simpler props - the audit doesn't have
 * issue-card strips or sub-card reasons, so the ISSUE AREA / REASON bands
 * collapse to a single optional "primary chip row" slot.
 *
 * Sections (top to bottom inside `IssuePanel`):
 *   - Header: title + helper text on the left, Reset filters on the right.
 *   - Primary chip row (optional): caller supplies the chip nodes. Keeps
 *     stock-band chip semantics (default-on, mutually exclusive bands)
 *     out of the declarative filter pipeline.
 *   - Filters row: search input + multiselect/select/toggle controls,
 *     rendered via the shared `exceptionFilterControls` helpers so the
 *     UI matches the rest of the inspector.
 *   - Secondary collapsible slot (optional): caller-owned demoted block
 *     such as the aging distribution accordion.
 */
import React, { useMemo } from 'react'
import { Box, Divider, Stack } from '@mui/material'

import IssuePanel from '../../../../../components/inspector/shared/IssuePanel'
import FilterPanelHeader from '../../../../../components/inspector/shared/FilterPanelHeader'
import {
  findSearchFilter,
  renderFilterControl,
  renderSearchInput,
} from '../../../../../components/inspector/shared/exceptionFilterControls'
import type { ExceptionFilterDef } from '../../../../../config/exceptions/types'

interface CatalogueFilterPanelProps {
  title: string
  helperText?: string
  onReset: () => void
  hasAnyActiveFilter: boolean
  /**
   * Caller-supplied node rendered above the filters row. In Dormant this
   * hosts the FOCUS issue-card strip.
   */
  primaryChips?: React.ReactNode
  filters: ExceptionFilterDef[]
  filterValues: Record<string, any>
  updateFilterValue: (key: string, value: any) => void
  /**
   * Optional node rendered flush-right on the filters row. Used by Dormant
   * to surface a read-only "Dormant relative to last N months" caption
   * without owning a parallel period control.
   */
  filterRowSuffix?: React.ReactNode
  /**
   * Demoted, caller-owned collapsible block (e.g. Aging distribution).
   * Rendered below a thin divider so it reads as secondary content.
   */
  secondaryCollapsible?: React.ReactNode
  /**
   * Row data backing dynamic-option filters. Multiselects declared with
   * `extractOptions` (instead of static `options`) resolve their dropdown
   * contents against this set.
   */
  rows?: Record<string, any>[]
}

const CatalogueFilterPanel: React.FC<CatalogueFilterPanelProps> = ({
  title,
  helperText,
  onReset,
  hasAnyActiveFilter,
  primaryChips,
  filters,
  filterValues,
  updateFilterValue,
  filterRowSuffix,
  secondaryCollapsible,
  rows,
}) => {
  // Resolve dynamic-option multiselects against the current row set so the
  // dropdown lists what's actually present. Mirrors the same step in
  // `DataViewFilterPanel`.
  const resolvedFilters = useMemo<ExceptionFilterDef[]>(() => {
    return filters.map((f) => {
      if (f.type === 'multiselect' && f.extractOptions) {
        return { ...f, options: f.extractOptions(rows ?? []) }
      }
      return f
    })
  }, [filters, rows])

  const searchFilter = findSearchFilter(resolvedFilters)
  const nonSearchFilters = resolvedFilters.filter((f) => f.type !== 'search')
  const hasFiltersRow = !!searchFilter || nonSearchFilters.length > 0

  return (
    <IssuePanel>
      <FilterPanelHeader
        title={title}
        helperText={helperText}
        onReset={onReset}
        hasAnyActiveFilter={hasAnyActiveFilter}
        tightBottom={!primaryChips && !hasFiltersRow && !secondaryCollapsible}
      />

      {primaryChips && (
        <Box sx={{ mb: hasFiltersRow ? 1.25 : 0 }}>{primaryChips}</Box>
      )}

      {hasFiltersRow && (
        <Stack
          direction="row"
          alignItems="center"
          sx={{ flexWrap: 'wrap', gap: 1.5, rowGap: 1.5 }}
        >
          {searchFilter && (
            <Box sx={{ minWidth: 280, flex: '0 1 320px' }}>
              {renderSearchInput({
                filter: searchFilter,
                value: (filterValues[searchFilter.key] ?? '') as string,
                onChange: (next) => updateFilterValue(searchFilter.key, next),
              })}
            </Box>
          )}
          {nonSearchFilters.map((f) =>
            renderFilterControl({
              filter: f,
              value:
                f.type === 'select'
                  ? (filterValues[f.key] ?? f.defaultValue ?? 'all')
                  : f.type === 'toggle'
                    ? !!(filterValues[f.key] ?? f.defaultEnabled)
                    : (filterValues[f.key] ?? (f.type === 'multiselect' ? [] : '')),
              onChange: (next) => updateFilterValue(f.key, next),
            }),
          )}
          {filterRowSuffix && (
            <Box
              sx={{
                ml: 'auto',
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0,
              }}
            >
              {filterRowSuffix}
            </Box>
          )}
        </Stack>
      )}

      {secondaryCollapsible && (
        <>
          <Divider sx={{ mt: 1.5, mb: 1.25, borderColor: 'rgba(0, 0, 0, 0.06)' }} />
          {secondaryCollapsible}
        </>
      )}
    </IssuePanel>
  )
}

export default CatalogueFilterPanel
